import React, { useState } from "react";
import styled from "@emotion/styled";
import { useSelector, useStore } from "react-redux";
import { executeGotoAction } from "src/hooks/navigation";
import { CoreReduxState } from "src/utils/redux/internal";
import { trackBuyflowEvent } from "src/utils/api/tracker";
import ConfirmationModal from "@components/header/ConfirmationModal";
import { css } from "@emotion/react";

import { telex280HoverMediaStyledThunk } from "src/design-system/styles/telex280HoverMediaWrapper";
import { useLocation } from "react-router";
import { PageSetHistoryState } from "@pageDefinitions/components/PageSetRouter";
import { getActivePage } from "src/pageDefinitions/pageSets";

const ExitButtonContainer = styled.div`
  position: absolute;
  top: 9px;
  right: 16px;
  z-index: 1000;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
  font-weight: 400;
  line-height: 1;
  color: #333;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;

  ${telex280HoverMediaStyledThunk(css`
    &:hover {
      background-color: #f8f8f8;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      transform: translateY(-1px);
    }
  `)}

  &:active {
    background-color: #f0f0f0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    transform: translateY(0);
  }
`;

declare module "@pageDefinitions/types" {
  export interface PageSetPageConfigProps {
    skipExitModalConfirmation?: boolean;
  }
}

const CloseIcon = () => <span style={{ fontSize: 28 }}>&times;</span>;

// Main component that manages Redux state and renders the exit button
export default function GlobalExitButton() {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { enabled, exitAction } = useSelector(
    (state: CoreReduxState) => state.globalExitConfiguration
  );

  const location = useLocation<PageSetHistoryState>();
  const store = useStore<CoreReduxState>();
  const { activePage } = getActivePage(
    location.pathname,
    store.getState(),
    location
  );
  const { skipExitModalConfirmation } = activePage?.props || {};

  // Only render exit button if we have both the flag enabled and a stored exit action
  if (!enabled || !exitAction) {
    return null;
  }

  const handleClick = () => {
    trackBuyflowEvent("BuyflowButtonClicked", {
      buttonName: "exitButton",
      pageId: window.location.pathname,
    });

    if (!skipExitModalConfirmation) setShowConfirmationModal(true);
    else executeGotoAction(exitAction);
  };

  const exit = () => {
    trackBuyflowEvent("BuyflowButtonClicked", {
      buttonName: "ConfirmExit",
      pageId: window.location.pathname,
    });
    executeGotoAction(exitAction);
  };

  const closeModal = () => {
    trackBuyflowEvent("BuyflowButtonClicked", {
      buttonName: "CancelExit",
      pageId: window.location.pathname,
    });
    setShowConfirmationModal(false);
  };

  return (
    <ExitButtonContainer>
      <Button data-cy="global-exit-button" onClick={handleClick}>
        <CloseIcon />
      </Button>
      {showConfirmationModal && (
        <ConfirmationModal
          onClose={closeModal}
          onPrimaryAction={exit}
          dataCy="confirmation-modal"
        />
      )}
    </ExitButtonContainer>
  );
}
